import React from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { fetchAllStats } from "../services/adminStats.service";
import { Stats, UseStatsReturn } from "../types/adminStats.types";
import { QUERY_KEY } from "../constants/constants";
import { useAdminRole } from "../modules/auth/hooks/useAdminRole";
import { AdminModule } from "../modules/auth/roles";

// ===========================================
// SIMPLIFIED STATS HOOK
// ===========================================

interface UseStatsOptions
  extends Omit<UseQueryOptions<Stats>, "queryKey" | "queryFn"> {
  enabled?: boolean;
}

/**
 * Simplified hook for fetching basic statistics counts
 * Only fetches video count, audio count, and categories count
 *
 * ACCESS: every endpoint behind this hook (`/api/media/stats*`,
 * `/api/categories/stats`) is gated on the ANALYTICS module, so the fetch is
 * suppressed for a principal without ANALYTICS read. The gate lives HERE rather
 * than in each caller because the callers are the sidebar (mounted on every
 * page) and the Dashboard (the index route, and the only page a grantless
 * manager can reach) — gating one and not the other just moves the 403 storm to
 * whichever was missed. Every present and future caller inherits it.
 */
export const useStats = (options: UseStatsOptions = {}): UseStatsReturn => {
  const toast = useToast();
  const { enabled = true, ...queryOptions } = options;
  const { canModule } = useAdminRole();
  const mayReadStats = canModule(AdminModule.ANALYTICS, "read");

  const query = useQuery({
    queryKey: [QUERY_KEY.ADMIN_STATS, "simplified"],
    queryFn: fetchAllStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    ...queryOptions,
    // Last word: a caller may narrow the query further, but never widen it past
    // what the signed-in principal is allowed to read.
    enabled: enabled && mayReadStats,
  });

  // Handle errors using useEffect instead of onError callback
  React.useEffect(() => {
    if (query.error) {
      console.error("Failed to fetch simplified stats:", query.error);
      toast({
        title: "Failed to load statistics",
        description: "Using fallback data. Please try again later.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [query.error, toast]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

// ===========================================
// DASHBOARD STATS HOOK (Simplified)
// ===========================================

/**
 * Simplified hook for dashboard display
 * Only provides basic counts without complex calculations
 */
export const useDashboardStats = () => {
  const { data, isLoading, isError, refetch } = useStats();

  return {
    videosCount: data?.videos.count || 0,
    audiosCount: data?.audios.count || 0,
    categoriesCount: data?.categories.count || 0,
    isLoading,
    isError,
    refetch,
  };
};

// ===========================================
// SIDEBAR STATS HOOK (Simplified)
// ===========================================

/**
 * Simplified hook for sidebar counts
 * Only provides basic counts for navigation menu
 *
 * The ANALYTICS gate lives in `useStats` itself, so this (mounted on every page)
 * and the Dashboard both inherit it — see the note there.
 */
export const useSidebarCounts = () => {
  const { data, isLoading } = useStats();

  return {
    videosCount: data?.videos.count || 0,
    audiosCount: data?.audios.count || 0,
    categoriesCount: data?.categories.count || 0,
    imagesCount: data?.images?.count || 0,
    isLoading,
  };
};
